import React, { useEffect, useState } from 'react';
import {
    Box,
    TextField,
    InputAdornment,
    Typography,
    IconButton,
    Button,
} from '@mui/material';
import colors from 'assets/theme/base/colors';
import pxToRem from 'assets/theme/function/px-to-rem';
import borders from 'assets/theme/base/borders';
import { ReactComponent as NounEye } from 'assets/images/sign-in/noun-eye.svg';
import { ReactComponent as Eye } from 'assets/images/sign-in/eye.svg';
import { useSignInMutation } from 'store/slices/staff-user/staff-user-auth-api';
import { useNavigate } from 'react-router-dom';
import typography from 'assets/theme/base/typography';
import { Controller, useForm } from 'react-hook-form';
import { useAppDispatch } from 'hooks/redux';
import { SignInRequest } from 'types/staff-user/staff-user';
import { openSnackbar } from 'store/slices/snackbar/snackbar-slice';
import DapniLogo from 'assets/images/uniwooca.png';

const SignIn: React.FC = () => {
    const dispatch = useAppDispatch();
    const { handleSubmit, control } = useForm<SignInRequest>();
    const [showPass, setShowPass] = useState<boolean>(false);

    const { borderRadius } = borders;

    const [signInApi, { isLoading, isSuccess, error, isError }] =
        useSignInMutation();
    const navigate = useNavigate();

    const onSubmit = (value: SignInRequest) => {
        signInApi({ ...value });
    };

    useEffect(() => {
        if (isSuccess) {
            navigate('/cards-sphere/sphere');
        }
    }, [isSuccess]);

    useEffect(() => {
        if (error && 'data' in error) {
            dispatch(
                openSnackbar({
                    message: error.data.message,
                    severity: 'error',
                    open: true,
                    position: {
                        vertical: 'top',
                        horizontal: 'right',
                    },
                }),
            );
        }
    }, [isError]);

    return (
        <>
            <Box
                maxWidth={pxToRem(500)}
                bgcolor={colors.secondary.main}
                borderRadius={borderRadius.threeXl}
                sx={{
                    py: { xs: pxToRem(20), md: pxToRem(30) },
                    px: { xs: pxToRem(10), md: pxToRem(50) },
                }}
            >
                <Box
                    display={'flex'}
                    alignItems={'center'}
                    justifyContent={'center'}
                >
                    <img
                        src={DapniLogo}
                        alt={'Logo Image'}
                        height={'80%'}
                        width={'80%'}
                        style={{ objectFit: 'cover' }}
                    />
                </Box>
                <Box
                    textAlign={'center'}
                    sx={{ py: { xs: pxToRem(15), md: pxToRem(20) } }}
                >
                    <Typography
                        variant={'h5'}
                        sx={{ fontWeight: typography.fontWeightXBold }}
                    >
                        SIGN IN
                    </Typography>
                </Box>
                <Box onSubmit={handleSubmit(onSubmit)} component="form">
                    <Controller
                        name="email"
                        control={control}
                        rules={{
                            required: 'email Is Required',
                            minLength: {
                                value: 3,
                                message: 'invalid Email',
                            },
                            pattern: {
                                value: /^\S+@\S+$/,
                                message: 'invalid Email',
                            },
                        }}
                        render={({
                            field: { name, onChange },
                            fieldState: { error },
                        }) => (
                            <TextField
                                label={'Email'}
                                name={name}
                                fullWidth
                                onChange={onChange}
                                error={Boolean(error)}
                                helperText={error?.message || ''}
                                sx={{ mt: pxToRem(20) }}
                            />
                        )}
                    />

                    <Controller
                        name="password"
                        control={control}
                        rules={{
                            required: 'password Is Required',
                            minLength: {
                                value: 8,
                                message:
                                    'password Must Be Least Characters Long',
                            },
                        }}
                        render={({
                            field: { name, onChange },
                            fieldState: { error },
                        }) => (
                            <TextField
                                label="Password"
                                name={name}
                                fullWidth
                                onChange={onChange}
                                error={Boolean(error)}
                                helperText={error?.message || ''}
                                type={showPass ? 'text' : 'password'}
                                sx={{ mt: pxToRem(20) }}
                                slotProps={{
                                    input: {
                                        'aria-label': 'password',
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton
                                                    onClick={() =>
                                                        setShowPass(
                                                            (current) =>
                                                                !current,
                                                        )
                                                    }
                                                    onMouseDown={(e) =>
                                                        e.preventDefault()
                                                    }
                                                    edge="end"
                                                >
                                                    {showPass ? (
                                                        <Eye />
                                                    ) : (
                                                        <NounEye />
                                                    )}
                                                </IconButton>
                                            </InputAdornment>
                                        ),
                                    },
                                }}
                            />
                        )}
                    />

                    <Box pt={pxToRem(30)}>
                        <Button
                            fullWidth
                            size={'large'}
                            variant={'contained'}
                            type={'submit'}
                            loading={isLoading}
                            sx={{ textTransform: 'capitalize' }}
                        >
                            Sign In
                        </Button>
                    </Box>
                </Box>
            </Box>
        </>
    );
};

export default SignIn;
